import { useMediaQuery } from "@mui/material";
import { Suspense, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Outlet, useNavigate } from "react-router-dom";

import getSessionDetails from "../../api/session";
import { apiEndpoints, httpMethods } from "../../constants/api.constants";
import useAPI from "../../hooks/useAPI";
import useI18N from "../../hooks/useI18N";
import { MUIStyled } from "../../shared/MUIStyled";
import SABox from "../../shared/SABox";
import theme from "../../themes/theme";
import { getNotifications } from "../../utils/functions";
import Notifications from "../Notifications";
import SuspenseFallback from "../SuspenseFallback";
import Header from "./header/Header";
import Sidebar from "./Sidebar";

const Main = MUIStyled(SABox)(({ theme, collapse, dir }) => {
  const isRTL = dir === "rtl";
  return {
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    backgroundColor: theme.palette.gray.light700,
    marginLeft: isRTL ? 0 : collapse ? 60 : 250,
    marginRight: isRTL ? (collapse ? 60 : 250) : 0,
    minHeight: "calc(100vh - 60px)",
    [theme.breakpoints.down("md")]: {
      marginLeft: isRTL ? 0 : 60,
      marginRight: isRTL ? 60 : 0,
    },
  };
});

const AppLayout = ({ userRole }) => {
  const { t, dir } = useI18N();
  const { api } = useAPI();
  const dispatch = useDispatch();
  const screenMd = useMediaQuery(theme.breakpoints.down("md"));
  const [collapse, setCollapse] = useState(false);
  const hasBankDetails = useSelector(
    (state) => state.app?.hasBankDetails ?? true,
  );
  const checkedBankDetails = useSelector(
    (state) => state.app?.checkedBankDetails ?? false,
  );
  const hasRiskLevels = useSelector(
    (state) => state.app?.hasRiskLevels ?? true,
  );
  const checkedRiskLevels = useSelector(
    (state) => state.app?.checkedRiskLevels ?? false,
  );
  const refreshList = useSelector((state) => state.app?.refreshList);
  const navigate = useNavigate();

  useEffect(() => {
    getNotifications();
    getSessionDetails();

    if (userRole === "ADMIN") {
      const checkBankDetails = async () => {
        try {
          const response = await api({
            method: httpMethods.get,
            endPoint: apiEndpoints.organizationBankAccount,
            needLoader: true,
          });
          let hasDetails = true;
          if (response?.status) {
            const bankDetails = response?.data;
            hasDetails = !!(
              bankDetails?.account_number &&
              bankDetails?.account_name &&
              bankDetails?.bank_name &&
              bankDetails?.iban_code &&
              bankDetails?.swift_code
            );
          }
          dispatch({
            type: "app/addAppData",
            payload: { name: "hasBankDetails", data: hasDetails },
          });
        } catch {
          dispatch({
            type: "app/addAppData",
            payload: { name: "hasBankDetails", data: true },
          });
        } finally {
          dispatch({
            type: "app/addAppData",
            payload: { name: "checkedBankDetails", data: true },
          });
        }
      };
      checkBankDetails();

      const checkRiskLevels = async () => {
        try {
          const response = await api({
            method: httpMethods.get,
            endPoint: apiEndpoints.riskLevel,
          });
          let hasLevels = true;
          if (response?.status && Array.isArray(response?.data?.results)) {
            hasLevels = response.data.results.length >= 3;
          }
          dispatch({
            type: "app/addAppData",
            payload: { name: "hasRiskLevels", data: hasLevels },
          });
        } catch {
          dispatch({
            type: "app/addAppData",
            payload: { name: "hasRiskLevels", data: true },
          });
        } finally {
          dispatch({
            type: "app/addAppData",
            payload: { name: "checkedRiskLevels", data: true },
          });
        }
      };
      checkRiskLevels();
    } else {
      dispatch({
        type: "app/addAppData",
        payload: { name: "checkedBankDetails", data: false },
      });
      dispatch({
        type: "app/addAppData",
        payload: { name: "hasBankDetails", data: true },
      });
      dispatch({
        type: "app/addAppData",
        payload: { name: "checkedRiskLevels", data: false },
      });
      dispatch({
        type: "app/addAppData",
        payload: { name: "hasRiskLevels", data: true },
      });
    }
  }, [api, userRole, dispatch]);
  useEffect(() => {
    if (
      userRole === "ADMIN" &&
      checkedBankDetails &&
      checkedRiskLevels &&
      hasBankDetails &&
      hasRiskLevels
    ) {
      dispatch({ type: "app/isRefreshList", payload: true });
    }
    if (
      userRole === "ADMIN" &&
      checkedBankDetails &&
      checkedRiskLevels &&
      (!hasBankDetails || !hasRiskLevels)
    ) {
      dispatch({ type: "app/isRefreshList", payload: false });
    }
  }, [
    userRole,
    checkedBankDetails,
    checkedRiskLevels,
    hasBankDetails,
    hasRiskLevels,
    dispatch,
  ]);

  const toggleCollapse = () => {
    setCollapse((prev) => (screenMd ? false : !prev));
  };

  return (
    <>
      {userRole === "ADMIN" &&
        checkedBankDetails &&
        checkedRiskLevels &&
        (!hasBankDetails || !hasRiskLevels) &&
        !refreshList && (
          <SABox
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              zIndex: 2000,
              background: "rgb(222 14 14 / 73%)",
              color: "#fff",
              textAlign: "center",
              padding: "12px 0",
              cursor: "pointer",
              fontWeight: 600,
              fontSize: "18px",
              transition: "background 0.2s",
              "&:hover": { background: "rgb(222 14 14 / 92%)" },
            }}
            onClick={() => {
              if (!hasBankDetails) {
                navigate("/settings/bank-setting");
              } else if (!hasRiskLevels) {
                navigate("/settings/selectable-field-settings?tab=risk-level");
              } else {
                navigate("/");
              }
            }}
          >
            {!hasBankDetails && !hasRiskLevels
              ? `${t("error-bar-title")} | ${t("error-bar-risk-level")}. ${t("click-here-to-update")}`
              : !hasBankDetails
                ? `${t("error-bar-title")}. ${t("click-here-to-update")}`
                : `${t("error-bar-risk-level")}. ${t("click-here-to-update")}`}
          </SABox>
        )}

      <Sidebar {...{ collapse, toggleCollapse, setCollapse, role: userRole }} />
      <Header {...{ collapse, toggleCollapse, role: userRole }} />
      <Main dir={dir} collapse={collapse} component="main">
        <Suspense fallback={<SuspenseFallback />}>
          <SABox
            sx={{
              padding: "24px",
              marginTop: "60px",
              display: "flex",
              flexDirection: "column",
              flexGrow: 1,
            }}
          >
            <Outlet />
          </SABox>
        </Suspense>
      </Main>
      <Notifications />
    </>
  );
};

export default AppLayout;
